import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiClient } from '@/api/client';

export function ForgotPasswordPage() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [tenantSlug, setTenantSlug] = useState(searchParams.get('tenant') ?? '');
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      await apiClient.post('/auth/password-reset/request', {
        email,
        tenant_slug: tenantSlug,
      });
    },
    onSuccess: () => setSubmitted(true),
    // Always show success — anti-enumeration (D-SSL-Pre-6 / D-T.2.1-1a)
    onError: () => setSubmitted(true),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !tenantSlug) return;
    mutation.mutate();
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-base px-4">
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0,159,253,1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,159,253,1) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <img src="/verigrc-shield-256.png" alt="VeriGRC" className="h-12 w-auto" />
          <div className="text-center">
            <h1 className="text-2xl font-bold tracking-tight text-text-primary font-display">VeriGRC</h1>
            <p className="mt-0.5 text-sm text-text-secondary">Unified Cyber GRC Platform</p>
          </div>
        </div>

        <div className="rounded-xl border border-border-subtle bg-bg-surface p-6 shadow-2xl">
          {submitted ? (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <CheckCircle2 className="h-10 w-10 text-accent-emerald" />
              <p className="text-base font-medium text-text-primary">Check your email</p>
              <p className="text-sm text-text-secondary">
                If an eligible account exists for{' '}
                <span className="font-medium text-text-primary">{email}</span>, you'll receive a
                password reset link within a few minutes.
              </p>
              <Link to="/login" className="mt-2 text-sm text-accent-cyan hover:underline">
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-5">
                <h2 className="text-lg font-semibold text-text-primary">Reset your password</h2>
                <p className="mt-0.5 text-sm text-text-secondary">
                  Enter your email and organization to receive a reset link.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div className="space-y-1.5">
                  <Label htmlFor="fp-email">Email address</Label>
                  <Input
                    id="fp-email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    autoFocus
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="fp-tenant">Organization</Label>
                  <Input
                    id="fp-tenant"
                    type="text"
                    placeholder="your-org"
                    value={tenantSlug}
                    onChange={(e) => setTenantSlug(e.target.value)}
                    autoComplete="organization"
                    required
                  />
                  <p className="text-xs text-text-secondary">
                    The same organization name you use to sign in.
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={mutation.isPending || !email || !tenantSlug}
                >
                  {mutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    'Send reset link'
                  )}
                </Button>

                <div className="text-center">
                  <Link to="/login" className="text-sm text-text-secondary hover:text-text-primary">
                    Back to sign in
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
